import { ClientApartment } from "../../../utils/api/apartmentRentalsAPI";

// ─── Formatting helpers ────────────────────────────────────────────────────────

export const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${new Intl.NumberFormat("de-DE").format(Math.round(n))}`;
  if (currency === "BAM") return `${new Intl.NumberFormat("bs-BA").format(Math.round(n))} KM`;
  return `${new Intl.NumberFormat("sr-RS").format(Math.round(n))} RSD`;
};

export const fmtPrice = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${Math.round(n)}`;
  if (currency === "BAM") return `${Math.round(n)}KM`;
  const k = Math.round(n);
  return k >= 1000 ? `${Math.round(k / 100) / 10}k` : `${k}`;
};

export const fmtDate = (y: number, m: number, d: number) =>
  `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`;

export interface CalDate { year: number; month: number; day: number }

export const pad2 = (n: number) => String(n).padStart(2, "0");

export const isoOf = (d: CalDate | null): string =>
  d ? `${d.year}-${pad2(d.month)}-${pad2(d.day)}` : "";

// ─── Night pricing ──────────────────────────────────────────────────────────────

// Returns effective price for a single night identified by YYYY-MM-DD
// Priority: 1) date-range rules  2) weekday rules  3) base price
export const effectiveNightPrice = (apt: ClientApartment, dateStr: string): number => {
  for (const rule of apt.pricingRules || []) {
    if (dateStr >= rule.from && dateStr <= rule.to) {
      return rule.type === "custom"
        ? rule.value
        : apt.pricePerNight * (1 - rule.value / 100);
    }
  }
  const dow = new Date(dateStr + "T12:00:00Z").getUTCDay();
  for (const rule of apt.weekdayRules || []) {
    if ((rule.days || []).includes(dow)) {
      return rule.type === "custom"
        ? rule.value
        : apt.pricePerNight * (1 - rule.value / 100);
    }
  }
  return apt.pricePerNight;
};

// For calendar cell display only — adds last-minute/early-booking on top of effectiveNightPrice.
// Each day is treated as a hypothetical check-in to determine if the timing discount would apply.
export const calendarNightPrice = (apt: ClientApartment, dateStr: string): number => {
  const base = effectiveNightPrice(apt, dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysAhead = Math.ceil((new Date(dateStr + "T00:00:00").getTime() - today.getTime()) / 86400000);
  if (apt.lastMinute?.enabled && daysAhead >= 0 && daysAhead <= apt.lastMinute.daysThreshold) {
    return base * (1 - apt.lastMinute.discount / 100);
  }
  if (apt.earlyBooking?.enabled && daysAhead >= apt.earlyBooking.daysThreshold) {
    return base * (1 - apt.earlyBooking.discount / 100);
  }
  return base;
};

// Returns the final price and a human-readable label explaining why the price is what it is.
export const getNightInfo = (apt: ClientApartment, dateStr: string): { price: number; label: string } => {
  const base = effectiveNightPrice(apt, dateStr);
  const withTiming = calendarNightPrice(apt, dateStr);
  const hasRule = Math.round(base) !== Math.round(apt.pricePerNight);
  const hasTiming = Math.round(withTiming) !== Math.round(base);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysAhead = Math.ceil(
    (new Date(dateStr + "T00:00:00").getTime() - today.getTime()) / 86400000
  );

  let timingLabel = "";
  if (hasTiming) {
    if (apt.lastMinute?.enabled && daysAhead >= 0 && daysAhead <= apt.lastMinute.daysThreshold) {
      timingLabel = `⚡ Last Minute −${apt.lastMinute.discount}%`;
    } else if (apt.earlyBooking?.enabled && daysAhead >= apt.earlyBooking.daysThreshold) {
      timingLabel = `🗓 Early Booking −${apt.earlyBooking.discount}%`;
    }
  }

  let ruleLabel = "";
  if (hasRule) {
    ruleLabel = base < apt.pricePerNight ? "__discount__" : "__specialPrice__";
    for (const rule of apt.pricingRules || []) {
      if (dateStr >= rule.from && dateStr <= rule.to) {
        if (rule.label) ruleLabel = rule.label;
        break;
      }
    }
    if (ruleLabel === "__discount__" || ruleLabel === "__specialPrice__") {
      const dow = new Date(dateStr + "T12:00:00Z").getUTCDay();
      for (const rule of apt.weekdayRules || []) {
        if ((rule.days || []).includes(dow) && rule.label) {
          ruleLabel = rule.label;
          break;
        }
      }
    }
  }

  let label: string;
  if (hasRule && hasTiming) {
    label = `${ruleLabel} + ${timingLabel}`;
  } else if (hasTiming) {
    label = timingLabel;
  } else if (hasRule) {
    label = ruleLabel;
  } else {
    label = "__standardPrice__";
  }

  return { price: Math.round(withTiming), label };
};

export interface NightGroup { label: string; nights: number; pricePerNight: number; total: number }
export interface StayResult { total: number; groups: NightGroup[]; hasDiscount: boolean }

// Compute stay totals night-by-night. Each night is priced independently via calendarNightPrice
// so timing discounts (last-minute / early-booking) apply only to nights within the timing window,
// matching exactly what the calendar cells show.
export const computeStay = (apt: ClientApartment, ci: CalDate, co: CalDate): StayResult => {
  const start = new Date(Date.UTC(ci.year, ci.month - 1, ci.day));
  const end = new Date(Date.UTC(co.year, co.month - 1, co.day));
  const totalNights = Math.round((end.getTime() - start.getTime()) / 86400000);

  // Night-count rule takes priority over per-date rules
  const nightCountRule = (apt.nightCountRules || []).find((r) => r.nights === totalNights);
  if (nightCountRule) {
    const price = Math.round(nightCountRule.pricePerNight);
    const total = price * totalNights;
    const label = nightCountRule.label || `${totalNights} ${totalNights === 1 ? "noć" : "noći"}`;
    return {
      total,
      groups: [{ label, nights: totalNights, pricePerNight: price, total }],
      hasDiscount: price < Math.round(apt.pricePerNight),
    };
  }

  const map: Record<string, { count: number; price: number; label: string }> = {};
  let current = new Date(start);
  while (current < end) {
    const dateStr = current.toISOString().split("T")[0];
    const { price, label } = getNightInfo(apt, dateStr);
    const key = `${price}||${label}`;
    if (!map[key]) map[key] = { count: 0, price, label };
    map[key].count++;
    current.setUTCDate(current.getUTCDate() + 1);
  }

  const groups: NightGroup[] = Object.values(map).map(({ count, price, label }) => ({
    label,
    nights: count,
    pricePerNight: price,
    total: count * price,
  }));

  const total = groups.reduce((s, g) => s + g.total, 0);
  const hasDiscount = groups.some((g) => g.pricePerNight < Math.round(apt.pricePerNight));
  return { total, groups, hasDiscount };
};

export const AMENITY_ICONS: Record<string, string> = {
  WiFi: "📶", Parking: "🅿️", "Air Conditioning": "❄️", Heating: "🔥",
  Kitchen: "🍳", "Washing Machine": "🌀", Balcony: "🏙", Pool: "🏊",
  Gym: "💪", TV: "📺", Dishwasher: "🫧", BBQ: "🔥",
};

export const AVATAR_COLORS = [
  "#264a3a", "#0e7490", "#b45309", "#3f6212",
  "#9d3b2e", "#4c3b6e", "#0284c7", "#a16207",
];

// ─── Booking window helper ────────────────────────────────────────────────────

export const getWindowCutoffDate = (apt: ClientApartment): Date | null => {
  const bw = apt.bookingWindow;
  if (!bw || bw.mode === "unlimited") return null;
  if (bw.mode === "fixed_date" && bw.fixedDate) {
    return new Date(bw.fixedDate + "T23:59:59.000Z");
  }
  if (bw.mode === "rolling" && bw.rollingDays) {
    const d = new Date();
    d.setDate(d.getDate() + bw.rollingDays);
    return d;
  }
  return null;
};

export const windowCutoffLabel = (apt: ClientApartment, shortMonths: string[], lang: string): string | null => {
  const bw = apt.bookingWindow;
  if (!bw || bw.mode === "unlimited") return null;
  const cutoff = getWindowCutoffDate(apt);
  if (!cutoff) return null;
  const d = cutoff.getDate();
  const m = cutoff.getMonth() + 1;
  const y = cutoff.getFullYear();
  if (bw.mode === "rolling") {
    return lang === "en"
      ? `Available ${bw.rollingDays} days ahead (until ${d}. ${shortMonths[m - 1]} ${y})`
      : `Dostupno ${bw.rollingDays} dana unaprijed (do ${d}. ${shortMonths[m - 1]} ${y})`;
  }
  return lang === "en"
    ? `Available until ${d}. ${shortMonths[m - 1]} ${y}`
    : `Dostupno do ${d}. ${shortMonths[m - 1]} ${y}`;
};
